import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, ListGroup, InputGroup, Spinner } from 'react-bootstrap';
import { IconSearch, IconArrowsExchange, IconChevronRight, IconChevronLeft } from '@tabler/icons-react';
import useGetSwitchCandidates from '../../../../services/booking/hooks/v4/useGetSwitchCandidates';
import useSwitchReservation from '../../../../services/booking/hooks/v4/useSwitchReservation';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import { SwitchCandidateType } from '../../../../services/booking/schema/SwitchReservationSchema';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import CurrencyInput from '../../../../shared/components/form/CurrencyInput';
import { Controller, useForm } from 'react-hook-form';
import PaginationComponent from '../../../../shared/components/pagination/PaginationComponent';

interface SwitchReservationModalProps {
    show: boolean;
    onClose: () => void;
    bookingId: string;
}

const SwitchReservationModal: React.FC<SwitchReservationModalProps> = ({ show, onClose, bookingId }) => {
    const [step, setStep] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCandidate, setSelectedCandidate] = useState<SwitchCandidateType | null>(null);

    // Hooks
    const { data: sourceBooking, isLoading: isLoadingSource } = useShowBooking({ bookingId });
    const { data: candidates, isLoading: isLoadingCandidates } = useGetSwitchCandidates({
        source_booking_room_id: bookingId,
        search: searchTerm,
        page: currentPage
    });


    const { mutate: switchReservation, isPending: isSubmitting } = useSwitchReservation();

    const { control, handleSubmit, reset, watch, setValue } = useForm({
        defaultValues: {
            source_rates: [] as { date: string; rate: number }[],
            target_rates: [] as { date: string; rate: number }[],
        }
    });

    useEffect(() => {
        if (show) {
            setStep(1);
            setSearchTerm('');
            setCurrentPage(1);
            setSelectedCandidate(null);
            reset();
        }
    }, [show, reset]);

    // Reset pagination when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Populate Source Rates
    useEffect(() => {
        if (sourceBooking) {
            setValue('source_rates', sourceBooking.data.rates.map(r => ({ date: r.date, rate: Number(r.rate) })));
        }
    }, [sourceBooking, setValue]);

    // Populate Target Rates from candidate data
    useEffect(() => {
        if (selectedCandidate?.rates) {
            setValue('target_rates', selectedCandidate.rates.map(r => ({ date: r.date, rate: Number(r.rate) })));
        } else if (selectedCandidate?.booking?.rates) {
            // Fallback to booking rates within candidate if rates array not directly available
            setValue('target_rates', selectedCandidate.booking.rates.map(r => ({ date: r.date, rate: Number(r.rate) })));
        }
    }, [selectedCandidate, setValue]);

    const handleSelectCandidate = (candidate: SwitchCandidateType) => {
        setSelectedCandidate(candidate);
        setStep(2);
    };

    const handleConfirmSwitch = (data: any) => {
        if (!selectedCandidate) return;

        const payload: any = {
            source_booking_room_id: bookingId,
            target_booking_room_id: selectedCandidate.id,
        };

        // Optional rates if room types differ or if manually edited
        const sourceRoomType = sourceBooking?.data?.room_type?.id;
        const targetRoomType = selectedCandidate.room_type_id || selectedCandidate.booking?.room_type?.id;

        if (sourceRoomType !== targetRoomType) {
            // Format rates as array of objects with only 'rate' field as requested
            payload.source_rates = data.source_rates.map((r: any) => ({ rate: r.rate }));
            payload.target_rates = data.target_rates.map((r: any) => ({ rate: r.rate }));
        }

        console.log("Switch Payload (Formatted):", payload);

        switchReservation(payload, {
            onSuccess: () => {
                onClose();
            }
        });
    };

    const renderStep1 = () => (
        <>
            <InputGroup className="mb-3">
                <InputGroup.Text><IconSearch size={18} /></InputGroup.Text>
                <Form.Control
                    placeholder="Search by candidate name or invoice..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {isLoadingCandidates ? (
                    <div className="text-center p-4"><Spinner animation="border" size="sm" /></div>
                ) : (
                    <>
                        <ListGroup variant="flush">
                            {candidates?.data.map((candidate) => (
                                <ListGroup.Item
                                    key={candidate.id}
                                    action
                                    onClick={() => handleSelectCandidate(candidate)}
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <div>
                                        <div className="fw-bold">{candidate.customer_name}</div>
                                        <small className="text-muted">#{candidate.invoice_number} | {candidate.room_type_name} ({candidate.room_number})</small>
                                        <div className="small">{candidate.checkin_date} - {candidate.checkout_date}</div>
                                    </div>
                                    <IconChevronRight size={18} className="text-muted" />
                                </ListGroup.Item>
                            ))}
                            {!candidates || candidates.data.length === 0 && (
                                <div className="text-center p-4 text-muted">No candidates found</div>
                            )}
                        </ListGroup>

                        {candidates && candidates.pagination.last_page > 1 && (
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={candidates.pagination.last_page}
                                onPageChange={(page) => setCurrentPage(page)}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );

    const renderStep2 = () => {
        return (
            <Row className="gy-4">
                <Col md={6}>
                    <div className="p-3 bg-light rounded h-100">
                        <h6 className="text-primary border-bottom pb-2 mb-3">Current Reservation (Source)</h6>
                        <div className="mb-2"><strong>Guest:</strong> {sourceBooking?.data.customer_name}</div>
                        <div className="mb-2"><strong>Room:</strong> {sourceBooking?.data.room?.room_number} ({sourceBooking?.data.room_type?.name})</div>
                        <div className="mb-2"><strong>Dates:</strong> {sourceBooking?.data.checkin_date} - {sourceBooking?.data.checkout_date}</div>
                        <div className="mb-2"><strong>Total:</strong> {formatPrice(Number(sourceBooking?.data.total_rate))}</div>
                    </div>
                </Col>
                <Col md={6}>
                    <div className="p-3 border rounded h-100">
                        <h6 className="text-success border-bottom pb-2 mb-3">Target Reservation (Switch To)</h6>
                        <div className="mb-2"><strong>Guest:</strong> {selectedCandidate?.customer_name}</div>
                        <div className="mb-2"><strong>Room:</strong> {selectedCandidate?.room_number} ({selectedCandidate?.room_type_name})</div>
                        <div className="mb-2"><strong>Dates:</strong> {selectedCandidate?.checkin_date} - {selectedCandidate?.checkout_date}</div>
                        <div className="mb-2"><strong>Total:</strong> {formatPrice(Number(selectedCandidate?.total_rate || selectedCandidate?.booking?.total_rate || 0))}</div>
                    </div>
                </Col>
                <Col xs={12} className="text-center mt-4">
                    <Button variant="outline-secondary" size="sm" onClick={() => setStep(1)} className="me-2">
                        <IconChevronLeft size={16} className="me-1" /> Back to Search
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => {
                        const sourceRoomType = sourceBooking?.data?.room_type?.id;
                        const targetRoomType = selectedCandidate?.room_type_id || selectedCandidate?.booking?.room_type?.id;
                        if (sourceRoomType !== targetRoomType) {
                            setStep(3);
                        } else {
                            handleConfirmSwitch({});
                        }
                    }}>
                        Next: Rate Adjustment <IconChevronRight size={16} className="ms-1" />
                    </Button>
                </Col>
            </Row>
        );
    };

    const renderStep3 = () => (
        <Row className="gy-4">
            <Col md={6}>
                <h6>Source Rates Adjustment</h6>
                {watch('source_rates')?.map((rate, index) => (
                    <div key={rate.date} className="mb-2">
                        <small className="text-muted d-block">{rate.date}</small>
                        <Controller
                            name={`source_rates.${index}.rate`}
                            control={control}
                            render={({ field }) => (
                                <CurrencyInput
                                    {...field}
                                    value={field.value}
                                    onChange={(_, val) => field.onChange(val)}
                                />
                            )}
                        />
                    </div>
                ))}
            </Col>
            <Col md={6}>
                <h6>Target Rates Adjustment</h6>
                {watch('target_rates')?.map((rate, index) => (
                    <div key={rate.date || index} className="mb-2">
                        <small className="text-muted d-block">{rate.date || `Nights ${index + 1}`}</small>
                        <Controller
                            name={`target_rates.${index}.rate`}
                            control={control}
                            render={({ field }) => (
                                <CurrencyInput
                                    {...field}
                                    value={field.value}
                                    onChange={(_, val) => field.onChange(val)}
                                />
                            )}
                        />
                    </div>
                ))}
            </Col>
            <Col xs={12} className="text-center mt-4">
                <Button variant="outline-secondary" size="sm" onClick={() => setStep(2)} className="me-2">
                    <IconChevronLeft size={16} className="me-1" /> Back to Comparison
                </Button>
                <Button variant="success" size="sm" onClick={handleSubmit(handleConfirmSwitch)} disabled={isSubmitting}>
                    {isSubmitting ? <Spinner animation="border" size="sm" /> : <><IconArrowsExchange size={18} className="me-1" /> Confirm Switch</>}
                </Button>
            </Col>
        </Row>
    );

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-5">
                    <IconArrowsExchange className="me-2 text-primary" />
                    Switch Reservation
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {isLoadingSource ? (
                    <FormSpinner />
                ) : (
                    <>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SwitchReservationModal;
